import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useState } from "react";
import { useTheme } from "@emotion/react";
import useAuth from "../../hooks/useAuth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { CounterField } from "../CounterField";
import { tokens } from "../../theme";
import MerchantSearchableSelector from "../MerchantSearchableSelector";
import { makeStyles } from "@mui/styles";
import { customBaseUrl } from "../../action/https";

const useStyles = makeStyles({
  radioGroup: {
    display: "flex",
    flexDirection: "row",
  },
});

const RequestTerminalModal = ({ isOpen, setModal, terminalTypes }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const [value, setValue] = useState(null);
  const [merchantDetails, setMerchantDetail] = useState(null);
  const [selectedValue, setSelectedValue] = useState("");
  const [terminals, setTerminals] = useState({});
  const [selected, setSelected] = useState([]);

  React.useEffect(() => {
    if (Array.isArray(terminalTypes)) {
      const initialTerminals = {};
      terminalTypes.forEach((type) => {
        if (type?.terminalName) {
          initialTerminals[type.terminalName] = 0;
        }
      });
      setTerminals(initialTerminals);
    }
  }, [terminalTypes]);
  const [total, setTotal] = useState(0);
  const colors = tokens(theme.palette.mode);
  const { success, snackError } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const classes = useStyles();

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const resolvedMerchantId = value?.merchantId || value?.id;
    const resolvedPhoneNumber = value?.phoneNumber || merchantDetails?.phoneNumber || "";
    const resolvedAddress = merchantDetails?.officeAddress || value?.officeAddress || "";
    const resolvedAccount = merchantDetails?.settlementBankAccount || "";
    const resolvedMerchantName = `${value?.firstName || ""} ${value?.lastName || value?.surname || ""}`.trim() || merchantDetails?.businessName || "";

    let requestedBy = "Admin";
    try {
      const userDetailsStr = localStorage.getItem("userDetails");
      if (userDetailsStr) {
        const userDetails = JSON.parse(userDetailsStr);
        requestedBy = userDetails.firstName || userDetails.username || "Admin";
      }
    } catch (err) {
      console.log(err);
    }

    try {
      const response = await axiosPrivate.post(
        `${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/request/admin`,
        {
          deliveryAddress: resolvedAddress,
          subTotal: total,
          fullOrPartPayment: "FULL",
          partPaymentAmount: 0,
          accountToDebit: selectedValue === "true" ? resolvedAccount : null,
          phoneNumber: resolvedPhoneNumber,
          merchantName: resolvedMerchantName,
          requestAmount: total,
          terminalTypes: selected.map((t) => ({
            terminalName: t.terminalName,
            terminalCategory: t.terminalCategory || t.terminalName,
            quantity: t.quantity,
            amount: Number(t.amount),
            terminalType: t.terminalName,
          })),
          requestId: `REQ_${Date.now()}`,
          isAdminRequest: true,
          requestedBy,
          userId: resolvedMerchantId,
        }
      );
      success(response?.data?.respDesc || response?.data?.message || "Request successful");
    } catch (error) {
      snackError(error?.response?.data?.message || error?.message || error);
      console.log(error);
    }
    handleClose();
  };

  const replaceTerminal = (existedTerminal, newTerminal) => {
    setSelected((prevItems) =>
      prevItems.map((item) =>
        item.terminalName === existedTerminal.terminalName ? newTerminal : item
      )
    );
  };

  const handleIncrement = (terminal) => {
    setTerminals((prev) => ({
      ...prev,
      [terminal.terminalName]: (prev[terminal.terminalName] || 0) + 1,
    }));

    setTotal((prev) => prev + Number(terminal.amount));

    const existedTerminal = selected.filter(
      (option) => option.terminalName == terminal.terminalName
    )[0];

    if (existedTerminal) {
      const newTerminal = {
        ...existedTerminal,
        quantity: existedTerminal.quantity + 1,
      };
      replaceTerminal(existedTerminal, newTerminal);
    } else {
      setSelected((prev) => [...prev, { ...terminal, quantity: 1 }]);
    }
  };

  const handleDecrement = (terminal) => {
    setTerminals((prev) => ({
      ...prev,
      [terminal.terminalName]: (prev[terminal.terminalName] || 0) - 1,
    }));

    setTotal((prev) => prev - Number(terminal.amount));

    const existedTerminal = selected.filter(
      (option) => option.terminalName == terminal.terminalName
    )[0];

    if (existedTerminal) {
      if (existedTerminal.quantity == 1) {
        setSelected((prev) =>
          prev.filter(
            (item) => item.terminalName !== existedTerminal.terminalName
          )
        );
      } else {
        const newTerminal = {
          ...existedTerminal,
          quantity: existedTerminal.quantity - 1,
        };
        replaceTerminal(existedTerminal, newTerminal);
      }
    }
  };

  const handleClose = () => {
    setModal((prev) => ({ ...prev, open: false }));
    setSelected([]);
    setValue(null);
    if (Array.isArray(terminalTypes)) {
      const resetTerminals = {};
      terminalTypes.forEach((type) => {
        if (type?.terminalName) {
          resetTerminals[type.terminalName] = 0;
        }
      });
      setTerminals(resetTerminals);
    } else {
      setTerminals({});
    }
    setTotal(0);
    setSelectedValue("");
  };

  const getMerchantDetails = async ({ merchantId }) => {
    try {
      const response = await axiosPrivate.get(
        "/agency-service/api/v1/agent/viewmerchantbymerchantid",
        {
          params: {
            merchantId,
          },
        }
      );
      setMerchantDetail(response?.data?.respBody);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectChange = (e, value) => {
    setValue(value);
    getMerchantDetails(value);
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          width: "55%",
          backgroundColor: colors.primary[400],
          p: 2,
          position: "relative",
          top: "50%",
          left: "55%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <form onSubmit={handleFormSubmit}>
          <Box padding={5}>
            <Typography
              variant="h3"
              sx={{ textAlign: "center", marginBottom: "40px" }}
            >
              Request For Terminal
            </Typography>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 4",
                },
                marginBottom: "10px",
              }}
            >
              {Array.isArray(terminalTypes) && terminalTypes.map((terminal, index) => {
                return (
                  <CounterField
                    key={index}
                    value={terminals[terminal.terminalName]}
                    name={terminal.terminalName}
                    label={terminal.terminalName}
                    onIncrement={() => handleIncrement(terminal)}
                    onDecrement={() => handleDecrement(terminal)}
                  />
                );
              })}
              <MerchantSearchableSelector
                value={value}
                handleChange={handleSelectChange}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>
            <Box display="flex" justifyContent="space-between" my={3}>
              <Typography variant="h6" color={colors.grey[300]}>
                Total Terminal Selected:
                <span
                  style={{
                    fontSize: 16,
                    color: colors.grey[100],
                    paddingLeft: 3,
                  }}
                >
                  {selected.length}
                </span>
              </Typography>
              <Typography variant="h6" color={colors.grey[300]}>
                Caution Fee:
                <span
                  style={{
                    fontSize: 16,
                    color: colors.grey[100],
                    paddingLeft: 3,
                  }}
                >
                  {total}
                </span>
              </Typography>
            </Box>

            <FormControl component="fieldset">
              <RadioGroup
                aria-label="debitCustomer"
                name="debitCustomer"
                value={selectedValue}
                className={classes.radioGroup}
                onChange={(e) => setSelectedValue(e.target.value)}
              >
                <FormControlLabel
                  value={true}
                  control={
                    <Radio
                      sx={{
                        color: colors.orangeAccent[300],
                        "& .MuiSvgIcon-root": {
                          fill: colors.orangeAccent[300],
                        },
                      }}
                    />
                  }
                  label="Debit Customer"
                />
                <FormControlLabel
                  value={false}
                  control={
                    <Radio
                      sx={{
                        color: colors.orangeAccent[300],
                        "& .MuiSvgIcon-root": {
                          fill: colors.orangeAccent[300],
                        },
                      }}
                    />
                  }
                  label="Dont Debit Customer"
                />
              </RadioGroup>
            </FormControl>

            <Button
              type="submit"
              fullWidth
              color="secondary"
              variant="contained"
              sx={{
                backgroundColor: colors.orangeAccent[700],
                color: colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: "12px 20px",
                marginTop: "20px",
              }}
            >
              Request Terminal
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default RequestTerminalModal;
